import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { ensureLoaded } from '@/lib/ensure-loaded';
import { reviewQueueQuery } from '@/services/review';
import { myRoleQuery } from '@/services/team-members';
import { roadVisibilityQuery } from '@/features/visibility/road-read';
import { parseVisibilitySearch, type VisibilitySearchInput } from '@/features/visibility/visibility-search';
import { OperationsRoad } from '@/features/operations/operator/road-provider';
import { prefetchOperatorRoad } from '@/features/operations/operator/road-prefetch';

// Operations' Road view (formerly Visibility, `/visibility`): her campaign's road — and, for an operator, the same road in
// his words, with Dreamwell's own work (`features/operations/operator`).
export const Route = createFileRoute('/_authed/operations/')({
  validateSearch: (search: VisibilitySearchInput & SearchSchemaInput) => parseVisibilitySearch(search),
  // Only the campaign changes what the page reads: opening a stage, Edit stages or a stop never re-runs the loader.
  loaderDeps: ({ search }) => ({ playbook: search.playbook }),
  // Her role decides whether the page opens at all; prefetch it so the page never flips from the road to "not in your role".
  // Then the two reads the page is built from (the campaign's and the review queue), so it renders in one wave. A role
  // without the page never reads it, and a refusal is the page's to show, not the loader's. The shell watches the role
  // and the queue too: a read it lets go of mid-way is cancelled, not refused, and waited for again (`ensureLoaded`).
  // While previewing a teammate, the campaign's read is the preview's (`roadVisibilityQuery`), the one the page shows.
  loader: async ({ context: { queryClient, team, user }, deps }) => {
    if (!team) return;
    const mine = await ensureLoaded(queryClient, myRoleQuery(team.id)).catch(() => null);
    if (mine && !mine.role.pages.visibility) return;
    await Promise.all([
      ensureLoaded(queryClient, reviewQueueQuery(team.id)).catch(() => null),
      queryClient.ensureQueryData(roadVisibilityQuery(team.id, user.id, deps.playbook ?? null)).catch(() => null),
      // An operator's: Draper's waiting work on the campaign (a client seat asks nothing more).
      prefetchOperatorRoad(queryClient, team, deps.playbook ?? null),
    ]);
  },
  component: RoadRoute,
});

function RoadRoute() {
  const search = Route.useSearch();
  return <OperationsRoad search={search} />;
}
